import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { APIClient } from '../../../utils/APIClient';
import { LocalStorage } from '../../../utils/LocalStorage';

const AgentSessionContext = createContext(null);

/**
 * @param {Object} props
 * @param {'global'|'client'|'portal'} props.entryPoint
 * @param {number|null} props.clientId - fijo para client/portal
 * @param {string|null} props.activeSessionId
 * @param {string|null} props.sessionBasePath - base de las rutas del chat (sesión = base + '/:id',
 *   nuevo chat = base). El tab IA de la ficha v2 pasa '/client/:id/ia/dashbito' para que el chat
 *   embebido navegue dentro del tab sin rebotar por la ruta legacy (que con flag ON re-redirige
 *   y remonta todo). Sin el prop, cada componente cae a su path histórico por entryPoint.
 * @param {React.ReactNode} props.children
 */
export const AgentSessionProvider = ({ entryPoint, clientId, activeSessionId, sessionBasePath = null, children }) => {
    const { t } = useTranslation();
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [visibilityFilter, setVisibilityFilter] = useState('all'); // 'all' | 'my' | 'shared'
    const [showArchived, setShowArchived] = useState(false);
    const [clientFilter, setClientFilter] = useState(null); // solo en global
    const [toast, setToast] = useState(null); // {message, undoAction} | null
    const toastTimerRef = useRef(null);

    const userId = LocalStorage.fetchUserId();

    // -- Fetch sessions --
    // Trae todas las sesiones; filtrado por archive/visibility es client-side
    // hasta que el backend soporte los params status/visibility
    const fetchSessions = useCallback(() => {
        setIsLoading(true);
        const params = {};
        if (clientId) params.agency_client_id = clientId;

        APIClient.getAgentSessions(params).then(data => {
            if (data && data.sessions) {
                setSessions(data.sessions);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    }, [clientId]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    // Refetch when active session changes (new session created)
    useEffect(() => {
        if (activeSessionId) {
            fetchSessions();
        }
    }, [activeSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // -- Toast helpers --
    const showToast = useCallback((message, undoAction) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ message, undoAction });
        toastTimerRef.current = setTimeout(() => setToast(null), 5000);
    }, []);

    const dismissToast = useCallback(() => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast(null);
    }, []);

    // -- Actions --
    const archiveSession = useCallback((sessionId, archive = true) => {
        setSessions(prev => prev.map(s =>
            s.session_id === sessionId ? { ...s, is_archived: archive } : s
        ));
        APIClient.archiveAgentSession(sessionId, archive).catch(() => {
            // Revertir en caso de error
            setSessions(prev => prev.map(s =>
                s.session_id === sessionId ? { ...s, is_archived: !archive } : s
            ));
            showToast(t('agent_chat.archive_error'));
        });
        return { sessionId, archive };
    }, [t, showToast]);

    const unarchiveSession = useCallback((sessionId) => {
        return archiveSession(sessionId, false);
    }, [archiveSession]);

    const deleteSession = useCallback((sessionId) => {
        return APIClient.deleteAgentSession(sessionId).then(() => {
            setSessions(prev => prev.filter(s => s.session_id !== sessionId));
            return true;
        }).catch(() => {
            showToast(t('agent_chat.delete_error'));
            return false;
        });
    }, [t, showToast]);

    const updateVisibility = useCallback((sessionId, visibility) => {
        return APIClient.updateAgentSessionVisibility(sessionId, visibility).then(data => {
            if (data) {
                setSessions(prev => prev.map(s =>
                    s.session_id === sessionId ? { ...s, visibility } : s
                ));
            }
            return data;
        }).catch(() => {
            showToast(t('agent_chat.visibility_error'));
        });
    }, [t, showToast]);

    const renameSession = useCallback((sessionId, name) => {
        return APIClient.renameAgentSession(sessionId, name).then(data => {
            if (data && data.name) {
                setSessions(prev => prev.map(s =>
                    s.session_id === sessionId ? { ...s, name: data.name } : s
                ));
            }
            return data;
        });
    }, []);

    const updateSessionLocal = useCallback((sessionId, updates) => {
        setSessions(prev => prev.map(s =>
            s.session_id === sessionId ? { ...s, ...updates } : s
        ));
    }, []);

    // -- Filtered sessions --
    const filteredSessions = useMemo(() => {
        let result = sessions;

        // Filtro por archivadas (client-side)
        if (!showArchived) {
            result = result.filter(s => !s.is_archived);
        }

        // Filtro por visibilidad (client-side)
        if (visibilityFilter === 'my') {
            result = result.filter(s => String(s.creator_id) === String(userId));
        } else if (visibilityFilter === 'shared') {
            result = result.filter(s => s.visibility && s.visibility !== 'PRIVATE');
        }

        // Filtro por cliente (solo global)
        if (entryPoint === 'global' && clientFilter) {
            result = result.filter(s => String(s.agency_client_id) === String(clientFilter));
        }

        // Filtro por búsqueda
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(s =>
                (s.name && s.name.toLowerCase().includes(q)) ||
                (s.client_name && s.client_name.toLowerCase().includes(q))
            );
        }

        // Ordenar por last_message_at desc
        result = [...result].sort((a, b) => {
            const dateA = a.last_message_at || a.created_at || '';
            const dateB = b.last_message_at || b.created_at || '';
            return dateB.localeCompare(dateA);
        });

        return result;
    }, [sessions, entryPoint, clientFilter, searchQuery, showArchived, visibilityFilter, userId]);

    // -- Active session object --
    const activeSession = useMemo(() => {
        if (!activeSessionId) return null;
        return sessions.find(s => s.session_id === activeSessionId) || null;
    }, [sessions, activeSessionId]);

    // Cantidad de sesiones archivadas (para mostrar/ocultar el toggle)
    const archivedCount = useMemo(() => sessions.filter(s => s.is_archived).length, [sessions]);

    const value = useMemo(() => ({
        // State
        sessions: filteredSessions,
        allSessions: sessions,
        isLoading,
        activeSessionId,
        activeSession,
        entryPoint,
        clientId,
        sessionBasePath,
        userId,
        archivedCount,

        // Filters
        searchQuery,
        setSearchQuery,
        visibilityFilter,
        setVisibilityFilter,
        showArchived,
        setShowArchived,
        clientFilter,
        setClientFilter,

        // Actions
        archiveSession,
        unarchiveSession,
        deleteSession,
        updateVisibility,
        renameSession,
        fetchSessions,
        updateSessionLocal,

        // Toast
        toast,
        showToast,
        dismissToast,
    }), [
        filteredSessions, sessions, isLoading, activeSessionId, activeSession, entryPoint, clientId, sessionBasePath, userId, archivedCount,
        searchQuery, visibilityFilter, showArchived, clientFilter,
        archiveSession, unarchiveSession, deleteSession, updateVisibility, renameSession, fetchSessions, updateSessionLocal,
        toast, showToast, dismissToast,
    ]);

    return (
        <AgentSessionContext.Provider value={value}>
            {children}
        </AgentSessionContext.Provider>
    );
};

export const useAgentSessions = () => {
    const ctx = useContext(AgentSessionContext);
    if (!ctx) throw new Error('useAgentSessions must be used within AgentSessionProvider');
    return ctx;
};
